import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../hooks/useAuth';
import { toast } from 'react-hot-toast';
import { Eye, EyeOff, User, Lock, Activity, Leaf, BarChart3, Building2, Shield } from 'lucide-react';
// import FEDEGANLogo from '../resources/Fedegan_fondo_verde.png';
import { motion, Variants } from 'framer-motion';

interface LoginForm {
  email: string;
  password: string;
}

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [_activeIcon, setActiveIcon] = useState(0);
  const { signIn } = useAuth();


  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<LoginForm>();

  const onSubmit = async (data: LoginForm) => {
    try {
      await signIn(data.email, data.password);
      toast.success('¡Inicio de sesión exitoso!');
      // La redirección se maneja automáticamente en signIn() según el rol del usuario
    } catch (error: any) {
      console.error('Login error:', error);
      if (error.code === 'auth/user-not-found') {
        toast.error('Usuario no encontrado');
      } else if (error.code === 'auth/wrong-password') {
        toast.error('Contraseña incorrecta');
      } else if (error.code === 'auth/invalid-email') {
        toast.error('Email inválido');
      } else {
        toast.error('Error en el inicio de sesión. Intenta nuevamente.');
      }
    }
  };

  // Variantes para animaciones
  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants: Variants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  // Rotar entre iconos cada 3 segundos
  useEffect(() => {
    const iconTypes = [Building2, BarChart3, Activity, Leaf];
    const interval = setInterval(() => {
      setActiveIcon((current) => (current + 1) % iconTypes.length);
    }, 3000);
    
    return () => clearInterval(interval);
  }, []);





  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #83BD1B 0%, #6BA016 20%, #B5D776 50%, #DAECBB 80%, #F0F8E8 100%)'
      }}
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div 
          className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(131,189,27,0.4) 0%, transparent 70%)' }}
          animate={{ 
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360]
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
        <motion.div 
          className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full opacity-15"
          style={{ background: 'radial-gradient(circle, rgba(181,215,118,0.5) 0%, transparent 70%)' }}
          animate={{ 
            scale: [1.2, 1, 1.2],
            rotate: [360, 180, 0]
          }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        />
        <motion.div 
          className="absolute top-20 left-20 w-20 h-20 rounded-full opacity-30"
          style={{ background: 'radial-gradient(circle, rgba(218,236,187,0.8) 0%, transparent 70%)' }}
          animate={{ 
            y: [-10, 10, -10],
            x: [-5, 5, -5]
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-32 right-32 w-16 h-16 rounded-full opacity-25"
          style={{ background: 'radial-gradient(circle, rgba(131,189,27,0.6) 0%, transparent 70%)' }}
          animate={{ 
            y: [10, -10, 10],
            x: [5, -5, 5]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <motion.div 
        className="container"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <motion.div
          className="card w-full max-w-3xl mx-auto grid grid-cols-1 overflow-hidden border border-white/20 relative min-h-[500px]"
          variants={itemVariants}
          whileHover={{ scale: 1.01, y: -5 }}
          transition={{ duration: 0.4 }}
          style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            boxShadow: '0 32px 64px rgba(131, 189, 27, 0.15), 0 16px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)'
          }}
        >
          {/* Glassmorphism border effect */}
          <div 
            className="absolute inset-0 rounded-3xl pointer-events-none"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
              border: '1px solid rgba(255,255,255,0.2)'
            }}
          />
          {/* Formulario */}
          <motion.div
            className="p-4 sm:p-6 lg:p-8 xl:p-12 flex flex-col justify-center h-auto relative"
            variants={itemVariants}
          >
            <div className="w-full max-w-md mx-auto relative z-10">
              {/* Header con título del sistema */}
              <motion.div
                className="text-center mb-8 sm:mb-10"
                variants={itemVariants}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div
                  className="inline-block px-6 py-3 rounded-2xl mb-6"
                  style={{
                    background: 'linear-gradient(135deg, #83BD1B 0%, #B5D776 100%)',
                    boxShadow: '0 8px 16px rgba(131, 189, 27, 0.3)'
                  }}
                >
                  <h1 className="text-lg sm:text-xl font-bold text-white">
                    Sistema de Trazabilidad Manual de Ganado
                  </h1>
                </div>
                <p className="text-sm text-gray-600 mb-1">
                  Trazabilidad Manual
                </p>
                <p className="text-xs text-gray-500">
                  Gestión profesional y moderna para el sector ganadero
                </p>
              </motion.div>

              <div className="mb-6 sm:mb-8">
                <motion.div
                  className="text-center mb-6 sm:mb-8"
                  variants={itemVariants}
                >
                  <motion.h2
                    className="text-2xl sm:text-3xl font-bold text-gray-800 mb-3"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.6 }}
                  >
                    Iniciar Sesión
                  </motion.h2>
                  <motion.div
                    className="w-16 h-1 mx-auto rounded-full mb-4"
                    style={{
                      background: 'linear-gradient(90deg, #83BD1B 0%, #B5D776 100%)'
                    }}
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 0.4, duration: 0.8 }}
                  />
                  <motion.p
                    className="text-gray-600 text-sm sm:text-base"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6, duration: 0.6 }}
                  >
                    Accede a tu cuenta para continuar
                  </motion.p>
                </motion.div>
              </div>

              {/* Formulario */}
              <form className="space-y-4 sm:space-y-6" onSubmit={handleSubmit(onSubmit)}>
                {/* Email */}
                <motion.div 
                  className="space-y-2"
                  variants={itemVariants}
                >
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Correo electrónico
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <motion.input
                      id="email"
                      type="email"
                      autoComplete="email"
                      className={`block w-full pl-12 pr-4 py-3 sm:py-4 border-2 rounded-xl focus:outline-none transition-all duration-300 text-sm sm:text-base ${
                        errors.email 
                          ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-4 focus:ring-red-100' 
                          : 'border-gray-200 bg-gray-50/50 focus:border-green-500 focus:ring-4 focus:ring-green-100 focus:bg-white'
                      }`}
                      style={{
                        backdropFilter: 'blur(10px)'
                      }}
                      placeholder="correo@ejemplo.com"
                      whileFocus={{ scale: 1.02 }}
                      transition={{ duration: 0.2 }}
                      {...register('email', {
                        required: 'El email es requerido',
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: 'Email inválido'
                        }
                      })}
                    />
                  </div>
                  {errors.email && (
                    <motion.p 
                      className="mt-1 text-sm text-red-500"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      {errors.email.message}
                    </motion.p>
                  )}
                </motion.div>
                
                {/* Password */}
                <motion.div 
                  className="space-y-2"
                  variants={itemVariants}
                >
                  <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                    Contraseña
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <motion.input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      className={`block w-full pl-12 py-3 sm:py-4 border-2 rounded-xl focus:outline-none transition-all duration-300 text-sm sm:text-base ${
                        errors.password 
                          ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-4 focus:ring-red-100' 
                          : 'border-gray-200 bg-gray-50/50 focus:border-green-500 focus:ring-4 focus:ring-green-100 focus:bg-white'
                      }`}
                      style={{
                        backdropFilter: 'blur(10px)',
                        paddingRight: '48px'
                      }}
                      placeholder="••••••••••"
                      whileFocus={{ scale: 1.02 }}
                      transition={{ duration: 0.2 }}
                      {...register('password', {
                        required: 'La contraseña es requerida',
                        minLength: {
                          value: 6,
                          message: 'La contraseña debe tener al menos 6 caracteres'
                        }
                      })}
                    />
                    <motion.button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center justify-center focus:outline-none group"
                      onClick={() => setShowPassword(!showPassword)}
                      whileTap={{ scale: 0.95 }}
                      tabIndex={-1}
                    >
                      <div
                        className="p-2 rounded-lg transition-all duration-300 group-hover:bg-green-50 group-hover:shadow-sm"
                        style={{
                          backdropFilter: 'blur(10px)'
                        }}
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5 text-gray-500 group-hover:text-green-600 transition-colors duration-300" />
                        ) : (
                          <Eye className="h-5 w-5 text-gray-500 group-hover:text-green-600 transition-colors duration-300" />
                        )}
                      </div>
                    </motion.button>
                  </div>
                  {errors.password && (
                    <motion.p 
                      className="mt-1 text-sm text-red-500"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      {errors.password.message}
                    </motion.p>
                  )}
                </motion.div>
                
                <motion.div
                  className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0 mb-4 sm:mb-6"
                  variants={itemVariants}
                >
                  <label
                    htmlFor="remember-me"
                    className="relative flex items-center gap-3 cursor-pointer group"
                    onClick={(e) => {
                      e.preventDefault();
                      setRememberMe(!rememberMe);
                    }}
                  >
                    <input
                      id="remember-me"
                      name="remember-me"
                      type="checkbox"
                      checked={rememberMe}
                      onChange={() => setRememberMe(!rememberMe)}
                      className="sr-only"
                    />
                    <div
                      className={`relative w-5 h-5 border-2 rounded-md transition-all duration-300 flex items-center justify-center ${
                        rememberMe
                          ? 'bg-gradient-to-br from-green-500 to-green-600 border-green-500'
                          : 'border-gray-300 group-hover:border-green-400'
                      }`}
                    >
                      <svg
                        className={`w-3 h-3 text-white transition-opacity duration-200 pointer-events-none ${
                          rememberMe ? 'opacity-100' : 'opacity-0'
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <span className="text-sm text-gray-700 select-none font-medium group-hover:text-green-600 transition-colors duration-200">
                      Recordar contraseña
                    </span>
                  </label>
                  <div className="text-sm">
                    <motion.a 
                      href="#" 
                      className="font-semibold text-green-600 hover:text-green-700 transition-colors duration-300 relative"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      ¿Olvidaste tu contraseña?
                      <motion.div 
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-600 origin-left"
                        initial={{ scaleX: 0 }}
                        whileHover={{ scaleX: 1 }}
                        transition={{ duration: 0.3 }}
                      />
                    </motion.a>
                  </div>
                </motion.div>
                
                {/* Submit button */}
                <motion.button
                  type="submit"
                  className="w-full relative flex justify-center items-center py-3 sm:py-4 px-6 border-0 text-sm sm:text-base font-semibold rounded-xl text-white transition-all duration-300 overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, #83BD1B 0%, #6BA016 50%, #B5D776 100%)',
                    boxShadow: '0 12px 24px rgba(131, 189, 27, 0.4), 0 4px 8px rgba(0, 0, 0, 0.1)'
                  }}
                  variants={itemVariants}
                  whileHover={{ 
                    scale: 1.02, 
                    y: -2,
                    boxShadow: '0 16px 32px rgba(131, 189, 27, 0.5), 0 8px 16px rgba(0, 0, 0, 0.15)'
                  }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Button content */}
                  <motion.div 
                    className="relative z-10 flex items-center gap-2 sm:gap-3"
                    initial={false}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Shield size={18} className="sm:w-5 sm:h-5" />
                    <span>Iniciar Sesión</span>
                  </motion.div>
                </motion.button>
              </form>
              
              {/* Footer */}
              <motion.div 
                className="text-center mt-8 sm:mt-12 lg:mt-16"
                variants={itemVariants}
              >
                <motion.p 
                  className="text-sm text-gray-500 mb-1 sm:mb-2"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  &copy; {new Date().getFullYear()} Sistema Natura Ganado Trazabilidad Manual

                </motion.p>
                <motion.p 
                  className="text-xs text-gray-400"
                  initial={{ opacity: 0.7 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
                >
                  Sistema de Trazabilidad Manual
                </motion.p>
              </motion.div>
              
              {/* Subtle background decoration */}
              <motion.div 
                className="absolute top-4 right-4 w-32 h-32 rounded-full opacity-5 pointer-events-none"
                style={{
                  background: 'radial-gradient(circle, #83BD1B 0%, transparent 70%)'
                }}
                animate={{ 
                  rotate: [0, 360],
                  scale: [1, 1.1, 1]
                }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              />
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
      
    </div>
  );
}